import {
  default as ChartIcon,
  default as SatisfactionIcon,
} from "@heroicons/react/24/outline/ChartBarIcon";
import PieChartIcon from "@heroicons/react/24/outline/ChartPieIcon";
import ChatIcon from "@heroicons/react/24/outline/ChatBubbleBottomCenterTextIcon";
import CheckIcon from "@heroicons/react/24/outline/CheckIcon";
import SmileIcon from "@heroicons/react/24/outline/FaceSmileIcon";
import HandIcon from "@heroicons/react/24/outline/HandRaisedIcon";
import RatingIcon from "@heroicons/react/24/outline/PresentationChartLineIcon";
import StarIcon from "@heroicons/react/24/outline/StarIcon";
import CircleIcon from "@heroicons/react/24/outline/StopCircleIcon";
import TrashIcon from "@heroicons/react/24/outline/TrashIcon";
import UserIcon from "@heroicons/react/24/outline/UserIcon";
import PlusIcon from "@heroicons/react/24/solid/PlusIcon";
import {
  Button,
  IconButton,
  List,
  ListItemButton,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { GlobalContext } from "App";
import {
  SyntheticEvent,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";
import { EAppReducerTypes } from "shared/helpers/AppContext";
import { useAsyncDispatch } from "shared/helpers/hooks/useAsyncDispatch";
import reorderDragDrop from "shared/helpers/reorderDragDrop";
import SharedDialog from "shared/ui/Dialog";
import { ERequestStatus } from "store/enums/index.enum";
import { ICampaignSurvey } from "store/interfaces/campaignDetails";
import {
  ChangeCampaignSurveyPositions,
  CreateSurvey,
  GetCampaignSurveyById,
  GetCampaignSurveyTemplateById,
  GetSurveyLogic,
  GetSurveysInitial,
  RemoveCampaignSurvey,
  selectCampaignInfo,
  selectCampaignSurveys,
  selectSelectedSurvey,
  setSelectedSurvey,
  setSelectedTemplateID,
} from "store/slicers/campaignDetail";
import {
  GetTranslationLanguages,
  selectTranslationLanguages,
} from "store/slicers/campaignTranslations";
import { setCampaignLoading, setLoading } from "store/slicers/common";
import {
  questionTypeAnswerScales,
  questionTypePlaceholders,
} from "../../constants";
import {
  CampaignSurveyColors,
  CampaignSurveyIcons,
  ECampaignSurveyType,
  SurveyTypeConfig,
  deleteCampaignSurveyWarningConfig,
} from "./constants";
import { isRatingQuestionWithText } from "./utils";

const defaultAnswers = [
  {
    value: "",
    position: 0,
  },
];

const LeftSidebar = () => {
  const { formState, setValue } = useFormContext();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const addContentMenuOpen = Boolean(anchorEl);
  const { dispatchContext } = useContext(GlobalContext);
  const dispatch = useAsyncDispatch();
  const [warningOpen, setWarningOpen] = useState(false);
  const campaignSurveys = useSelector(selectCampaignSurveys);
  const [surveyList, setSurveyList] = useState<ICampaignSurvey[]>([]);
  const selectedSurvey = useSelector(selectSelectedSurvey);
  const campaignInfo = useSelector(selectCampaignInfo);
  const translationLanguages = useSelector(selectTranslationLanguages);
  // Track the current survey being fetched to prevent race conditions
  const fetchingSurveyRef = useRef<number | null>(null);
  const isMountedRef = useRef(true);
  const alreadyHasType = useCallback(
    (type: ECampaignSurveyType) => {
      return surveyList.find((i) => i.type === Number(type));
    },
    [surveyList]
  );

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClickContentAdd = async (e: SyntheticEvent<HTMLLIElement>) => {
    dispatch(setLoading(true));
    e.stopPropagation();
    const type = e.currentTarget.id;

    handleClose();

    const campaingLanguageCode = translationLanguages.find(
      (i) => i.languageId === campaignInfo?.defaultLanguageID
    )?.code;

    const titlePlaceholder =
      questionTypePlaceholders?.[campaingLanguageCode]?.[type];

    const answerScales =
      questionTypeAnswerScales?.[campaingLanguageCode]?.[type];

    const baseConfig = SurveyTypeConfig[type] || {};

    // Merge answer scales into metricConfig if available
    if (answerScales && baseConfig.metricConfig) {
      baseConfig.metricConfig = {
        ...baseConfig.metricConfig,
        metricLeftText: answerScales.likely,
        metricRightText: answerScales.unlikely,
      };
    }

    const formData = {
      ...baseConfig,
      title: titlePlaceholder || "",
      languageID: campaignInfo?.defaultLanguageID,
      campaignID: String(campaignInfo.id),
      buttonText: "Next",
      type,
      selected: false,
      position:
        type === ECampaignSurveyType.Welcome
          ? 0
          : (surveyList[surveyList.length - 2]?.position || 1) + 1,
      completed: isRatingQuestionWithText(type),
      isRequired: type !== ECampaignSurveyType.Welcome,
    };

    const { meta, payload } = await dispatch(CreateSurvey(formData));
    if (meta.requestStatus !== ERequestStatus.FULFILLED) {
      dispatch(setLoading(false));
      return;
    }

    await dispatch(
      GetSurveysInitial({
        id: campaignInfo.id,
        language_id: campaignInfo.defaultLanguageID,
      })
    );
    setValue("answers", defaultAnswers);
    dispatch(setLoading(false));
    dispatch(setSelectedSurvey(String(payload.surveyId)));
  };

  const onDragEnd = ({ destination, source, draggableId }) => {
    if (!destination || destination.index === source.index) {
      return;
    }

    const actualSurvey = surveyList.find((i) => i.id === draggableId);

    if (
      !(actualSurvey.type === Number(ECampaignSurveyType.Final)) &&
      destination.index === surveyList.length - 1
    ) {
      return;
    }

    const newItems = reorderDragDrop(
      surveyList,
      source.index,
      destination.index
    );
    setSurveyList(newItems);
    const newItemsSorted = [...newItems].sort(
      (a, b) => a.position - b.position
    );
    dispatch(
      ChangeCampaignSurveyPositions({
        data: {
          surveyPositions: newItemsSorted,
          languageID: campaignInfo?.defaultLanguageID,
        },
        id: campaignInfo.id,
      })
    );
  };

  const handleRemove = async () => {
    const removeThunk = await dispatch(
      RemoveCampaignSurvey({
        id: selectedSurvey,
        language_id: campaignInfo.defaultLanguageID,
      })
    );
    if (removeThunk.meta.requestStatus !== ERequestStatus.FULFILLED) {
      return;
    }

    const { meta, payload } = await dispatch(
      GetSurveysInitial({
        id: campaignInfo.id,
        language_id: campaignInfo.defaultLanguageID,
      })
    );
    if (meta.requestStatus !== ERequestStatus.FULFILLED) {
      return;
    }
    dispatch(setSelectedSurvey(String(payload[0].id)));
  };

  const handleSetOpen = (questionId: number) => {
    dispatchContext({
      type: EAppReducerTypes.SET_UNSAVED_MODAL_DATA,
      payload: {
        isOpen: true,
        questionId,
      },
    });
  };

  const handleClickSurvey = async (id: number) => {
    if (selectedSurvey === id) {
      return;
    }
    if (formState.isDirty) {
      handleSetOpen(id);
      return;
    }
    fetchSurveyData(id);
    await dispatch(setCampaignLoading(true));
    await dispatch(setSelectedSurvey(id));
  };

  const fetchSurveyData = useCallback(
    async (surveyId: number) => {
      if (!surveyId || !campaignInfo?.defaultLanguageID) {
        return;
      }

      // Mark this survey as being fetched
      fetchingSurveyRef.current = surveyId;

      try {
        await Promise.all([
          dispatch(
            GetCampaignSurveyById({
              id: surveyId,
              language_id: campaignInfo?.defaultLanguageID,
            })
          ),
          dispatch(GetCampaignSurveyTemplateById(surveyId)),
          dispatch(
            GetSurveyLogic({
              id: surveyId,
              languageID: campaignInfo?.defaultLanguageID,
            })
          ),
        ]);

        // Only update state if this is still the current survey being fetched
        // This prevents race conditions where an older request completes after a newer one
        if (fetchingSurveyRef.current === surveyId && isMountedRef.current) {
          await dispatch(setSelectedTemplateID(null));
          await dispatch(setCampaignLoading(false));
        }
      } catch (error) {
        // Only update state if this is still the current survey
        if (fetchingSurveyRef.current === surveyId && isMountedRef.current) {
          await dispatch(setCampaignLoading(false));
        }
      } finally {
        // Clear the ref if this was the last request
        if (fetchingSurveyRef.current === surveyId) {
          fetchingSurveyRef.current = null;
        }
      }
    },
    [campaignInfo?.defaultLanguageID, dispatch]
  );

  // const handleClickSuccess = useCallback(
  //   async (id: number) => {
  //     // Prevent clicking the same survey again or if already fetching
  //     if (selectedSurvey === id || fetchingSurveyRef.current === id) {
  //       return;
  //     }

  //     await dispatch(setCampaignLoading(true));
  //     await dispatch(setSelectedSurvey(id));
  //     // Fetch data directly instead of relying on useEffect
  //     // This ensures we have control over the async flow
  //     // fetchSurveyData will manage the fetchingSurveyRef internally
  //     await fetchSurveyData(id);
  //   },
  //   [dispatch, selectedSurvey, fetchSurveyData]
  // );

  const getOptionIcon = (type: number) => {
    const Comp = CampaignSurveyIcons[type];
    return (
      <Box mr={1} display="flex" alignItems="center">
        <Comp height={20} width={20} color="white" />
      </Box>
    );
  };

  useEffect(() => {
    if (!campaignSurveys.length) {
      return;
    }
    setSurveyList(campaignSurveys);
  }, [campaignSurveys, dispatch]);

  // This effect now only handles initial load or when selectedSurvey changes externally
  // Most navigation is handled directly in handleClickSuccess
  useEffect(() => {
    if (selectedSurvey && fetchingSurveyRef.current !== selectedSurvey) {
      // Only fetch if not already fetching this survey
      // This handles cases where selectedSurvey changes from external sources
      fetchSurveyData(selectedSurvey);
    }
  }, [fetchSurveyData, selectedSurvey]);

  // useEffect(() => {
  //   if (campaignDetails.isSuccess && campaignDetails.questionId) {
  //     handleClickSuccess(campaignDetails.questionId);
  //     dispatchContext({
  //       type: EAppReducerTypes.SET_UNSAVED_MODAL_DATA,
  //       payload: {
  //         questionId: 0,
  //         isOpen: false,
  //         isSuccess: false,
  //       },
  //     });
  //   }
  // }, [
  //   campaignDetails?.id,
  //   campaignDetails?.isSuccess,
  //   campaignDetails?.questionId,
  //   dispatchContext,
  //   handleClickSuccess,
  // ]);

  useEffect(() => {
    dispatch(GetTranslationLanguages());
  }, [dispatch]);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      fetchingSurveyRef.current = null;
    };
  }, []);

  return (
    <Box p={1}>
      <Box mt={2}>
        <Button
          fullWidth
          variant="outlined"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          startIcon={<PlusIcon height={20} />}
        >
          Add content
        </Button>
        <Menu
          anchorEl={anchorEl}
          open={addContentMenuOpen}
          onClose={handleClose}
        >
          <Box p={2}>
            <Typography color="text.secondary">Questions</Typography>
          </Box>
          {/* {!alreadyHasType(ECampaignSurveyType.ChooseLang) && (
            <MenuItem
              id={ECampaignSurveyType.ChooseLang}
              onClick={handleClickContentAdd}
            >
              <Box
                borderRadius={"4px"}
                display={"flex"}
                sx={{
                  backgroundColor: CampaignSurveyColors[
                    ECampaignSurveyType.ChooseLang
                  ] as string,
                }}
                alignItems={"center"}
                p={"2px"}
                mr={1}
              >
                <LanguageIcon height={20} color="white" width={40} />
              </Box>
              Choose language
            </MenuItem>
          )} */}
          {!alreadyHasType(ECampaignSurveyType.Welcome) && (
            <MenuItem
              id={ECampaignSurveyType.Welcome}
              onClick={handleClickContentAdd}
            >
              <Box
                borderRadius={"4px"}
                display={"flex"}
                sx={{
                  backgroundColor: CampaignSurveyColors[
                    ECampaignSurveyType.Welcome
                  ] as string,
                }}
                alignItems={"center"}
                p={"2px"}
                mr={1}
              >
                <HandIcon height={20} color="white" width={40} />
              </Box>
              Welcome
            </MenuItem>
          )}

          <MenuItem
            id={ECampaignSurveyType.SingleChoice}
            onClick={handleClickContentAdd}
          >
            <Box
              borderRadius={"4px"}
              display={"flex"}
              sx={{
                backgroundColor: CampaignSurveyColors[
                  ECampaignSurveyType.SingleChoice
                ] as string,
              }}
              alignItems={"center"}
              p={"2px"}
              mr={1}
            >
              <CircleIcon color="white" height={20} width={40} />
            </Box>
            Select One
          </MenuItem>
          <MenuItem
            id={ECampaignSurveyType.MultipleChoice}
            onClick={handleClickContentAdd}
          >
            <Box
              borderRadius={"4px"}
              display={"flex"}
              sx={{
                backgroundColor: CampaignSurveyColors[
                  ECampaignSurveyType.MultipleChoice
                ] as string,
              }}
              alignItems={"center"}
              p={"2px"}
              mr={1}
            >
              <CheckIcon color="white" height={20} width={40} />
            </Box>
            Select Multiple
          </MenuItem>
          <MenuItem
            id={ECampaignSurveyType.Comment}
            onClick={handleClickContentAdd}
          >
            <Box
              borderRadius={"4px"}
              display={"flex"}
              sx={{
                backgroundColor: CampaignSurveyColors[
                  ECampaignSurveyType.Comment
                ] as string,
              }}
              alignItems={"center"}
              p={"2px"}
              mr={1}
            >
              <ChatIcon color="white" height={20} width={40} />
            </Box>
            Comment
          </MenuItem>
          {!alreadyHasType(ECampaignSurveyType.ContactInformation) && (
            <MenuItem
              id={ECampaignSurveyType.ContactInformation}
              onClick={handleClickContentAdd}
            >
              <Box
                borderRadius={"4px"}
                display={"flex"}
                sx={{
                  backgroundColor: CampaignSurveyColors[
                    ECampaignSurveyType.ContactInformation
                  ] as string,
                }}
                alignItems={"center"}
                p={"2px"}
                mr={1}
              >
                <UserIcon color="white" height={20} width={40} />
              </Box>
              Contact Information
            </MenuItem>
          )}
          <Box p={2}>
            <Typography color="text.secondary">Ratings</Typography>
          </Box>

          {!alreadyHasType(ECampaignSurveyType.Nps) && (
            <MenuItem
              id={ECampaignSurveyType.Nps}
              onClick={handleClickContentAdd}
            >
              <Box
                borderRadius={"4px"}
                display={"flex"}
                sx={{
                  backgroundColor: CampaignSurveyColors[
                    ECampaignSurveyType.Nps
                  ] as string,
                }}
                alignItems={"center"}
                p={"2px"}
                mr={1}
              >
                <ChartIcon color="white" height={20} width={40} />
              </Box>
              NPS
            </MenuItem>
          )}
          {!alreadyHasType(ECampaignSurveyType.ServiceQualityScore) && (
            <MenuItem
              id={ECampaignSurveyType.ServiceQualityScore}
              onClick={handleClickContentAdd}
            >
              <Box
                borderRadius={"4px"}
                display={"flex"}
                sx={{
                  backgroundColor: CampaignSurveyColors[
                    ECampaignSurveyType.ServiceQualityScore
                  ] as string,
                }}
                alignItems={"center"}
                p={"2px"}
                mr={1}
              >
                <SmileIcon color="white" height={20} width={40} />
              </Box>
              eNPS
            </MenuItem>
          )}

          {!alreadyHasType(ECampaignSurveyType.CustomerEffortScore) && (
            <MenuItem
              id={ECampaignSurveyType.CustomerEffortScore}
              onClick={handleClickContentAdd}
            >
              <Box
                borderRadius={"4px"}
                display={"flex"}
                sx={{
                  backgroundColor: CampaignSurveyColors[
                    ECampaignSurveyType.CustomerEffortScore
                  ] as string,
                }}
                alignItems={"center"}
                p={"2px"}
                mr={1}
              >
                <PieChartIcon color="white" height={20} width={40} />
              </Box>
              Customer Effort score
            </MenuItem>
          )}

          {!alreadyHasType(ECampaignSurveyType.CustomerSatisfactionScore) && (
            <MenuItem
              id={ECampaignSurveyType.CustomerSatisfactionScore}
              onClick={handleClickContentAdd}
            >
              <Box
                borderRadius={"4px"}
                display={"flex"}
                sx={{
                  backgroundColor: CampaignSurveyColors[
                    ECampaignSurveyType.CustomerSatisfactionScore
                  ] as string,
                }}
                alignItems={"center"}
                p={"2px"}
                mr={1}
              >
                <SatisfactionIcon color="white" height={20} width={40} />
              </Box>
              Customer Satisfaction score
            </MenuItem>
          )}

          <MenuItem
            id={ECampaignSurveyType.Rating}
            onClick={handleClickContentAdd}
          >
            <Box
              borderRadius={"4px"}
              display={"flex"}
              sx={{
                backgroundColor: CampaignSurveyColors[
                  ECampaignSurveyType.Rating
                ] as string,
              }}
              alignItems={"center"}
              p={"2px"}
              mr={1}
            >
              <RatingIcon color="white" height={20} width={40} />
            </Box>
            Custom
          </MenuItem>

          {!alreadyHasType(ECampaignSurveyType.CustomStar) && (
            <MenuItem
              id={ECampaignSurveyType.CustomStar}
              onClick={handleClickContentAdd}
            >
              <Box
                borderRadius={"4px"}
                display={"flex"}
                sx={{
                  backgroundColor: CampaignSurveyColors[
                    ECampaignSurveyType.CustomStar
                  ] as string,
                }}
                alignItems={"center"}
                p={"2px"}
                mr={1}
              >
                <StarIcon color="white" height={20} width={40} />
              </Box>
              Star
            </MenuItem>
          )}
        </Menu>
      </Box>
      <List>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="droppable-list">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {surveyList?.map((survey, index) => {
                  const getLabel = () => {
                    let label = survey.title;

                    if (label.length > 30) {
                      return label.substring(0, 30) + "...";
                    } else {
                      return label;
                    }
                  };
                  return (
                    <Draggable
                      isDragDisabled={
                        survey.type === Number(ECampaignSurveyType.Final)
                      }
                      key={survey.id}
                      draggableId={survey.id}
                      index={index}
                    >
                      {(provided) => (
                        <ListItemButton
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          selected={selectedSurvey === survey.id}
                          onClick={() => handleClickSurvey(survey.id)}
                        >
                          <Box
                            display="flex"
                            justifyContent={"space-between"}
                            alignItems={"center"}
                            width="100%"
                          >
                            <Box display={"flex"} alignItems={"center"}>
                              <Box
                                px={1}
                                display="flex"
                                justifyContent={"center"}
                                alignItems={"center"}
                                color="primary.secondary"
                                sx={{
                                  backgroundColor: String(
                                    CampaignSurveyColors[survey.type]
                                  ),
                                  borderRadius: "4px",
                                }}
                              >
                                {getOptionIcon(survey.type)}
                                <Typography color={"white"}>
                                  {index + 1}
                                </Typography>
                              </Box>
                              <Typography fontSize={14} ml={1}>
                                {getLabel()}
                              </Typography>
                            </Box>
                            {String(survey.type) !==
                              ECampaignSurveyType.Final && (
                              <Box>
                                <IconButton
                                  onClick={() => setWarningOpen(true)}
                                >
                                  <TrashIcon height={20} width={20} />
                                </IconButton>
                              </Box>
                            )}
                          </Box>
                        </ListItemButton>
                      )}
                    </Draggable>
                  );
                })}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        <SharedDialog
          open={warningOpen}
          setOpen={setWarningOpen}
          onSuccess={handleRemove}
          textConfig={deleteCampaignSurveyWarningConfig}
        />
      </List>
    </Box>
  );
};

export default LeftSidebar;
